'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { ValidationMessage } from '@/ui/form/validation-message/ValidationMessage.client'
import { Loader } from '@/ui/loaders/loader/Loader'
import { cva } from 'class-variance-authority'
import { useCallback, useId, useMemo } from 'react'
import { useFocusRing, useHover } from 'react-aria'
import { TextArea as AriaTextArea } from 'react-aria-components'
import type { PrivateUiInputProps } from '../../_shared/AbstractInput.client'
import { SharedUILabel } from '../../_shared/SharedUILabel.client'

const textAreaInputStyles = cva(
  'relative flex min-h-11 items-stretch rounded-sm border-px bg-white text-brand-2-700 outline-none outline-2 outline-offset-2 hd:border-1.5',
  {
    variants: {
      hasError: {
        true: 'border-error-fg',
        false: 'border-brand-2-200',
      },
      isFocusVisible: {
        true: 'outline-brand-1-800',
      },
      isFocusedInteractive: {
        true: 'border-brand-2-900',
      },
      isHoveredInteractive: {
        true: 'border-brand-1-900',
      },
      isDisabledOrReadOnly: {
        true: 'bg-brand-2-50 text-brand-2-400',
      },
    },
  },
)

export type TextAreaProps = StrictOmit<
  PrivateUiInputProps<
    string,
    {
      isReadOnly: boolean
      className?: string
      autocomplete?: string
      placeholder?: string
      classNameInput?: string
    } & StrictPick<UiComponentProps, 'aria-label' | 'id' | 'key'>
  >,
  'maxLength'
> &
  (
    | {
        maxRows: number
        maxLength?: never
      }
    | {
        maxRows?: never
        maxLength: number
      }
  )

export function TextArea({
  name,
  label,
  maxLength,
  error,
  isSubmitting,
  isRequired,
  isReadOnly,
  isLoading,
  className,
  autocomplete = 'off',
  placeholder,
  classNameInput,
  onChange,
  value,
  maxRows,
  id,
  isDisabled,
  onFocus,
  'aria-label': ariaLabel,
}: TextAreaProps) {
  const hasError = !!error?.message
  const shouldDisplayError = !isLoading && !isDisabled && hasError
  const isDisabledOrReadOnly = !!isDisabled || isReadOnly

  const generatedId = useId()
  const inputId = id ?? generatedId
  const errorId = shouldDisplayError && `${inputId}-error`

  const { focusProps, isFocused, isFocusVisible } = useFocusRing({ within: true })
  const { hoverProps, isHovered } = useHover({})
  const isFocusedInteractive = !shouldDisplayError && !isDisabledOrReadOnly && isFocused
  const isHoveredInteractive =
    !shouldDisplayError && !isDisabledOrReadOnly && !isFocused && isHovered

  const inputProps = useMemo(
    () => ({
      maxLength,
      name,
      type: 'text',
      id: inputId,
      placeholder,
      autoComplete: autocomplete,
      readOnly: isReadOnly,
      disabled: isDisabled,
      onFocus,
      className: twMerge(
        'min-h-11 w-full max-w-full grow appearance-none bg-clear px-2 py-[0.875rem] outline-none', // 14px because of the figma design
        isDisabled || isSubmitting || isLoading ? 'resize-none' : 'resize-y',
      ),
      'aria-describedby': errorId || undefined,
      'aria-label': ariaLabel,
      'data-1pignore': true,
    }),
    [
      maxLength,
      name,
      inputId,
      placeholder,
      autocomplete,
      isReadOnly,
      isDisabled,
      isSubmitting,
      isLoading,
      onFocus,
      errorId,
      ariaLabel,
    ],
  )

  const valueByUnit = useCallback(
    (value: string) => {
      if (!value || value.length === 0) {
        return 0
      }

      if (maxRows) {
        return value.split('\n').length
      }

      return value.length
    },
    [maxRows],
  )

  const withRowLimits = useCallback(
    (value: string) => {
      if (maxRows && value.split('\n').length > maxRows) {
        return value.split('\n').slice(0, maxRows).join('\n')
      }

      return value
    },
    [maxRows],
  )

  return (
    <div
      className={twMerge(
        'flex min-w-[15rem] flex-col gap-1',
        (isLoading || isSubmitting) && 'pointer-events-none',
        className,
      )}
      data-invalid={hasError ? 'true' : undefined}
    >
      <SharedUILabel inputId={inputId} label={label} required={isRequired} />
      <div
        {...focusProps}
        {...hoverProps}
        className={twMerge(
          textAreaInputStyles({
            hasError: shouldDisplayError,
            isFocusVisible: isFocusVisible && isFocusedInteractive,
            isFocusedInteractive,
            isHoveredInteractive,
            isDisabledOrReadOnly,
          }),
          classNameInput || 'grow',
        )}
      >
        <AriaTextArea
          {...inputProps}
          value={value}
          onChange={(e) =>
            maxRows ? onChange(withRowLimits(e.target.value)) : onChange(e.target.value)
          }
        />
        {isLoading && (
          <span className="absolute right-0 top-0 grid h-full w-6 place-items-center">
            <Loader className="text-primary" />
          </span>
        )}
      </div>
      <div className="flex items-start justify-between gap-x-2">
        {shouldDisplayError && error?.message && (
          <ValidationMessage id={errorId || undefined} status="error">
            {error.message}
          </ValidationMessage>
        )}
        {!isDisabledOrReadOnly && (
          <span className="mt-0.5 grow text-right text-sm font-medium leading-light">
            {valueByUnit(value)}/{maxRows || maxLength}
          </span>
        )}
      </div>
    </div>
  )
}
